/**
 * The deal's value, editable in place — the Value row of the sidebar's Deal
 * block.
 *
 * What it shows is the EFFECTIVE value: a rep's number when one has been set,
 * otherwise the number survivorship derived from ServiceTitan or Pipedrive. The
 * rep's number lives in its own operator-owned columns on the canonical deal,
 * which the recompute never touches, so an edit here survives every later
 * source event. Canonical Hickory deals are the system of record for it;
 * nothing writes it back to Pipedrive, and on a Pipedrive-sourced deal the row
 * says so rather than refusing the edit.
 *
 * The system's number never disappears. When it differs from the rep's, one
 * line names where it came from and offers it back in a click — which CLEARS
 * the rep's number rather than copying the system's over it, so later changes
 * to the estimate keep flowing through. There is no other retire rule: a rep's
 * number stands until a rep changes it.
 *
 * One formatter (`formatDealValue`) for this row and the header chip, so the
 * two cannot print the same deal differently.
 */

import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PRESS_ICON } from "@/components/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCapabilities } from "@/hooks/use-capabilities";
import { TAP_AREA } from "@/lib/tap-area";
import { cn } from "@/lib/utils";
import { useUpdateDealValue } from "@/hooks/use-deal-board";
import { formatBusinessDate } from "@/lib/business-date";
import { usePipedriveFrozen } from "@/hooks/use-brands";
import type { DealDetail } from "@/hooks/use-deal-board";

import { formatDealValue, parseValueInput } from "./deal-value";
import { ValueMismatchChip } from "./value-mismatch-chip";

export function DealValueEditor({ deal }: { deal: DealDetail }) {
  const { can } = useCapabilities();
  const editable = can("deal_management.triage");
  const update = useUpdateDealValue();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const value = formatDealValue(deal.value_amount, deal.value_currency);
  const systemValue = formatDealValue(deal.system_value_amount, deal.value_currency);
  const repSet = deal.value_set_at != null;
  // Only when the two numbers actually differ: a rep re-asserting the system's
  // own figure has nothing to be offered back.
  const showSystemHint = repSet && systemValue != null && deal.system_value_amount !== deal.value_amount;
  // The one branch that reads the source SLUG. The ruling is that reps on a
  // Pipedrive-linked deal are told they are de-synced, not stopped — on a brand
  // KNOWN to still be on Pipedrive. One that has left it has nothing to be
  // de-synced from, and the sentence would be the only Pipedrive mention on the
  // page; one whose fact has not loaded yet gets no sentence either.
  const pipedriveLive = usePipedriveFrozen(deal.tenant_id) === false;
  const pipedriveSourced = deal.value_source_system === "pipedrive" && pipedriveLive;
  const pending = update.isPending;

  const save = async (amount: number | null) => {
    try {
      // `mutateAsync` rather than `mutate` with callbacks: TanStack drops a
      // superseded call's callbacks, and closing the editor is exactly the
      // callback a rapid second save would lose.
      await update.mutateAsync({ dealId: deal.id, valueAmount: amount });
      setEditing(false);
      setError(null);
    } catch {
      toast.error("Couldn't save the deal value.");
    }
  };

  const startEditing = () => {
    setDraft(deal.value_amount != null && deal.value_amount > 0 ? String(deal.value_amount) : "");
    setError(null);
    setEditing(true);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = parseValueInput(draft);
    if (parsed === "invalid") {
      setError("Enter a dollar amount above zero.");
      return;
    }
    // An empty field on a deal with no rep value is not a change worth a
    // round-trip.
    if (parsed === null && !repSet) {
      setEditing(false);
      return;
    }
    void save(parsed);
  };

  return (
    <div className="space-y-0.5">
      {editing ? (
        <form onSubmit={submit} className="flex items-center gap-1" aria-busy={pending}>
          <Input
            size="sm"
            inputMode="decimal"
            aria-label="Deal value"
            aria-invalid={error ? true : undefined}
            autoFocus
            value={draft}
            disabled={pending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setEditing(false);
            }}
            className="h-11 md:h-7 w-28 tabular-nums"
          />
          <Button
            type="submit"
            size="icon"
            {...PRESS_ICON}
            variant="ghost"
            className="size-11 md:size-7"
            aria-label={pending ? "Saving value" : "Save value"}
            disabled={pending}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            type="button"
            size="icon"
            {...PRESS_ICON}
            variant="ghost"
            className="size-11 md:size-7"
            aria-label="Cancel editing value"
            disabled={pending}
            onClick={() => setEditing(false)}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </form>
      ) : (
        <div className="flex items-center gap-1.5">
          {value ? (
            <span className="font-medium tabular-nums">{value}</span>
          ) : (
            <span className="text-muted-foreground">Not set</span>
          )}
          {deal.value_mismatch ? <ValueMismatchChip /> : null}
          {editable ? (
            <button
              type="button"
              aria-label="Edit value"
              onClick={startEditing}
              className={cn("relative cursor-pointer text-muted-foreground hover:text-foreground", TAP_AREA)}
            >
              <Pencil className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </div>
      )}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {repSet ? (
        <p className="text-xs text-muted-foreground">
          Set by {deal.value_set_by_name ?? "a rep"} on {formatBusinessDate(deal.value_set_at as string)}
        </p>
      ) : null}
      {showSystemHint ? (
        <p className="text-xs text-muted-foreground">
          {deal.value_source ?? "The system"} says <span className="tabular-nums">{systemValue}</span>
          {editable ? (
            <>
              {" — "}
              <button
                type="button"
                onClick={() => void save(null)}
                disabled={pending}
                className="cursor-pointer font-medium text-primary hover:underline disabled:opacity-50"
              >
                Use it
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      {pipedriveSourced && editable && (editing || repSet) ? (
        <p className="text-xs text-muted-foreground">Changes here stay in HickoryOS and do not sync to Pipedrive.</p>
      ) : null}
    </div>
  );
}
